import type { RegexRule, STRegexPlacementNumber } from './llmTypes';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';

export type RegexApplyOptions = {
  placement?: STRegexPlacementNumber;
  isMarkdown?: boolean;
  isPrompt?: boolean;
  isEdit?: boolean;
  depth?: number;
  macroContext?: Record<string, string>;
};

// ============================================
// Regex 输出处理器
// ============================================

export class RegexProcessor {
  static apply(text: string, rules: RegexRule[], options: RegexApplyOptions = {}): string {
    if (!text || rules.length === 0) return text;

    if (options.placement !== undefined) {
      return this.applyByPlacement(text, rules, options.placement, options);
    }

    // 与酒馆顺序保持一致：推理先于 AI 输出
    const withReasoning = this.applyByPlacement(text, rules, 6, options);
    return this.applyByPlacement(withReasoning, rules, 2, options);
  }

  static applyForPlacement(
    text: string,
    rules: RegexRule[],
    placement: STRegexPlacementNumber,
    options: Omit<RegexApplyOptions, 'placement'> = {},
  ): string {
    return this.applyByPlacement(text, rules, placement, options);
  }

  private static applyByPlacement(
    text: string,
    rules: RegexRule[],
    placement: STRegexPlacementNumber,
    options: Omit<RegexApplyOptions, 'placement'> = {},
  ): string {
    if (!text || rules.length === 0) return text;

    const macroContext = this.normalizeMacroContext(options.macroContext);
    const expandedRules = [...rules]
      .filter(rule => this.shouldRun(rule, placement, options))
      .sort((a, b) => {
        const orderA = this.getOrder(a);
        const orderB = this.getOrder(b);
        if (orderA !== orderB) return orderA - orderB;
        return a.id.localeCompare(b.id);
      });

    let output = text;
    for (const rule of expandedRules) {
      try {
        const regex = this.buildRegex(rule, macroContext);
        if (!regex) continue;
        output = output.replace(regex, (...args: unknown[]) => this.buildReplacement(rule, args, macroContext));
      } catch (error) {
        LogService.warn(
          'RegexProcessor',
          `规则执行失败，已跳过: ${rule.scriptName} (${rule.id})`,
          (error as Error).message,
        );
      }
    }
    return output;
  }

  private static shouldRun(
    rule: RegexRule,
    placement: STRegexPlacementNumber,
    options: Omit<RegexApplyOptions, 'placement'>,
  ): boolean {
    const enabled = !rule.disabled;
    const source = this.getPatternSource(rule).trim();
    if (!enabled || !source) return false;

    const isMarkdown = options.isMarkdown === true;
    const isPrompt = options.isPrompt === true;
    const appliesByMode =
      (rule.markdownOnly && isMarkdown) ||
      (rule.promptOnly && isPrompt) ||
      (!rule.markdownOnly && !rule.promptOnly && !isMarkdown && !isPrompt);
    if (!appliesByMode) return false;

    if (options.isEdit === true && !rule.runOnEdit) return false;
    if (typeof options.depth === 'number') {
      if (
        !Number.isNaN(Number(rule.minDepth)) &&
        rule.minDepth !== null &&
        Number(rule.minDepth) >= -1 &&
        options.depth < Number(rule.minDepth)
      ) {
        return false;
      }
      if (
        !Number.isNaN(Number(rule.maxDepth)) &&
        rule.maxDepth !== null &&
        Number(rule.maxDepth) >= 0 &&
        options.depth > Number(rule.maxDepth)
      ) {
        return false;
      }
    }

    return this.getSTPlacements(rule).includes(placement);
  }

  private static getOrder(rule: RegexRule): number {
    return Number.isFinite(rule.order) ? rule.order : 100;
  }

  private static getPatternSource(rule: RegexRule): string {
    return rule.findRegex ?? '';
  }

  private static getReplaceTemplate(rule: RegexRule): string {
    const template = rule.replaceString ?? '';
    return template.replace(/{{match}}/gi, '$0');
  }

  private static getSTPlacements(rule: RegexRule): number[] {
    return Array.isArray(rule.placement) && rule.placement.length > 0 ? rule.placement : [];
  }

  private static buildRegex(rule: RegexRule, macroContext: Record<string, string>): RegExp | null {
    const source = this.getRegexSource(rule, macroContext);
    const literalMatch = source.match(/^\/([\s\S]*)\/([a-z]*)$/i);
    if (literalMatch) {
      return new RegExp(literalMatch[1], literalMatch[2]);
    }

    return new RegExp(source);
  }

  private static buildReplacement(rule: RegexRule, args: unknown[], macroContext: Record<string, string>): string {
    const [match, ...rest] = args;
    const fullMatch = String(match ?? '');
    const groups = rest[rest.length - 1];

    const replacement = this.getReplaceTemplate(rule);
    if (!replacement.includes('$')) return replacement;

    const replaced = replacement.replace(/\$(\d+)|\$<([^>]+)>/g, (_all, numeric, named) => {
      let captured = '';
      if (numeric) {
        const index = Number(numeric);
        captured = String(rest[index - 1] ?? '');
      } else if (named && groups && typeof groups === 'object') {
        captured = String((groups as Record<string, unknown>)[named] ?? '');
      } else {
        captured = fullMatch;
      }
      return this.applyTrimStrings(captured, rule.trimStrings, macroContext);
    });
    return this.resolveMacros(replaced, macroContext);
  }

  private static applyTrimStrings(value: string, trimStrings: string[], macroContext: Record<string, string>): string {
    let output = value;
    for (const trimItem of trimStrings) {
      if (!trimItem) continue;
      const resolvedTrim = this.resolveMacros(trimItem, macroContext);
      output = output.split(resolvedTrim).join('');
    }
    return output;
  }

  private static getRegexSource(rule: RegexRule, macroContext: Record<string, string>): string {
    const source = this.getPatternSource(rule);
    switch (Number(rule.substituteRegex)) {
      case 1:
        return this.resolveMacros(source, macroContext);
      case 2:
        return this.escapeForRegex(this.resolveMacros(source, macroContext));
      case 0:
      default:
        return source;
    }
  }

  private static normalizeMacroContext(source?: Record<string, string>): Record<string, string> {
    const context: Record<string, string> = {};
    if (!source) return context;
    for (const [key, value] of Object.entries(source)) {
      const normalizedKey = key.trim().toLowerCase();
      const normalizedValue = String(value ?? '').trim();
      if (!normalizedKey || !normalizedValue) continue;
      context[normalizedKey] = normalizedValue;
    }
    return context;
  }

  private static resolveMacros(text: string, macroContext: Record<string, string>): string {
    if (!text || Object.keys(macroContext).length === 0) return text;
    return text.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (token, key: string) => {
      const normalizedKey = key.trim().toLowerCase();
      const value = macroContext[normalizedKey];
      return value !== undefined ? value : token;
    });
  }

  private static escapeForRegex(text: string): string {
    return text.replace(/[\n\r\t\v\f\0.^$*+?{}[\]\\/|()]/g, segment => {
      switch (segment) {
        case '\n':
          return '\\n';
        case '\r':
          return '\\r';
        case '\t':
          return '\\t';
        case '\v':
          return '\\v';
        case '\f':
          return '\\f';
        case '\0':
          return '\\0';
        default:
          return `\\${segment}`;
      }
    });
  }
}
